import React, { useState, useEffect } from "react";
import { Grid, SelectField, Option, Alert, Checkbox } from "@deere/fuel-react";
import { QUESTION_TYPES, SELF_SERVICE_DEPENDENCY_INFO_MSG, SELF_SERVICE_DEPENDENCY_INFO_MSG_STATE_DD, SELF_SERVICE_DEPENDENCY_INFO_TITLE, SELFSERVICE_FIELD_NAMES, SURVEYPART_SECTION_TYPES } from "../../../../utils/surveyLiterals";
import { getSelectionsDependson } from "../../../../utils/utils";
import { isGeneratedMatrixCopy } from "../../../../utils/matrixQuestionUtils";

const editableQuestions = (questions) => questions.filter((que) => !isGeneratedMatrixCopy(questions, que));

const DependencyTab = ({ payloadData, selectedSec, selectedQue, changeHandler }) => {
  const [localState, setLocalState] = useState({ section: null, question: null, selections: [] });
  const [sectionsToChooseFrom, setSectionsToChooseFrom] = useState([]);
  const [questionsToChooseFrom, setQuestionsToChooseFrom] = useState([]);
  const [selectionsToChooseFrom, setSelectionsToChooseFrom] = useState([]);
  const currentSection = payloadData && payloadData.sections ? payloadData.sections.find((section) => section.code === selectedSec) : null;

  useEffect(() => {
    if (payloadData && payloadData.sections) {
      for (let i = 0; i < payloadData.sections.length; i++) {
        const index = payloadData.sections[i].questions.findIndex((q) => q.code === selectedQue.codeQuestionDepends);
        if (index > -1) {
          setLocalState({ 
            section: payloadData.sections[i],
            question: selectedQue.codeQuestionDepends,
            selections: getSelectionsDependson(selectedQue).filter((sel) => sel !== "0"),
          });
          const queToChooseFrom = payloadData.sections[i].code === selectedSec ? editableQuestions(payloadData.sections[i].questions).filter((que) => que.showOrder < selectedQue.showOrder) : editableQuestions(payloadData.sections[i].questions);
          setQuestionsToChooseFrom(queToChooseFrom);
          const que = payloadData.sections[i].questions.find((que) => que.code === selectedQue.codeQuestionDepends);
          setSelectionsToChooseFrom(que.selections);
          break;
        }
      }
    }
  }, [payloadData.sections]);

  useEffect(() => {
    if (currentSection && payloadData && payloadData.sections) {
      const sec = payloadData.sections.filter((sec) => sec.showOrder <= currentSection.showOrder && sec.type !== SURVEYPART_SECTION_TYPES.SUPPLIER && sec.type !== SURVEYPART_SECTION_TYPES.RESPONSES);
      setSectionsToChooseFrom(sec);
    }
  }, [currentSection, payloadData]);

  const dependencyChangeHandler = (e) => {
    switch (e.target.name) {
      case SELFSERVICE_FIELD_NAMES.Section:
        setLocalState({ section: e.target.value, question: null, selections: [] });
        const queToChooseFrom = e.target.value
          ? e.target.value.code === selectedSec
            ? editableQuestions(e.target.value.questions).filter((que) => que.showOrder < selectedQue.showOrder)
            : editableQuestions(e.target.value.questions)
          : [];
        setQuestionsToChooseFrom(queToChooseFrom);
        setSelectionsToChooseFrom([]);
        changeHandler({target: {name: SELFSERVICE_FIELD_NAMES.codeQuestionDepends, value: null}}, selectedQue.code);
        break;
      case SELFSERVICE_FIELD_NAMES.Question:
        setLocalState((prevState) => {
          return { ...prevState, question: e.target.value, selections: [] };
        });
        const que = localState.section.questions.find((que) => que.code === e.target.value);
        e.target.value && e.target.value !== "" ? setSelectionsToChooseFrom(que.selections) : setSelectionsToChooseFrom([]);
        e.target.name = SELFSERVICE_FIELD_NAMES.codeQuestionDepends;
        changeHandler(e, selectedQue.code);
        break;
      case SELFSERVICE_FIELD_NAMES.Selections: {
        const selections = (Array.isArray(e.target.value) ? e.target.value : []).filter((sel) => sel !== null && sel !== "");
        setLocalState((prevState) => {
          return { ...prevState, selections };
        });
        changeHandler({ target: { name: SELFSERVICE_FIELD_NAMES.idSelectionsDependson, value: selections } }, selectedQue.code);
        break;
      }
    }
  };

  const isCountryDisabled = (que) => {
    if (selectedQue.type === QUESTION_TYPES.SEL_STATE) {
      if (que.type === QUESTION_TYPES.SEL_CNTRY || que.type === QUESTION_TYPES.SEL_CTY_UW) {
        const index = currentSection && currentSection.questions ? currentSection.questions.findIndex((q) => q.codeQuestionDepends === que.code) : -1;
        return index > -1 ? true : false;
      }
      return true;
    }
    return false;
  };

  return (
    <Grid container mt={4} mb={4}>
      <Alert
        variant="info"
        title={SELF_SERVICE_DEPENDENCY_INFO_TITLE}
        sx={{ width: "100%", padding: "6px 8px", marginBottom: "20px", "& .MuiTypography-body2": { fontSize: "12px" }, "& .MuiTypography-body1": { fontSize: "14px" } }}
      >
        {SELF_SERVICE_DEPENDENCY_INFO_MSG}
      </Alert>
      {selectedQue.type === QUESTION_TYPES.SEL_STATE && (
        <Alert
          variant="info"
          sx={{ width: "100%", padding: "6px 8px", marginBottom: "20px", "& .MuiTypography-body2": { fontSize: "12px" }, "& .MuiTypography-body1": { fontSize: "14px" } }}
        >
          {SELF_SERVICE_DEPENDENCY_INFO_MSG_STATE_DD}
        </Alert>
      )}
      <SelectField
        label={SELFSERVICE_FIELD_NAMES.Section}
        sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
        onChange={(e) => {
          e.target.name = SELFSERVICE_FIELD_NAMES.Section;
          dependencyChangeHandler(e);
        }}
        value={localState.section}
      >
        <Option value={null}>
          Select
        </Option>
        {sectionsToChooseFrom.sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0)).map((sec) => (
          <Option key={sec.code} value={sec}>
            {sec.description}
          </Option>
        ))}
      </SelectField>
      <SelectField
        label={SELFSERVICE_FIELD_NAMES.Question}
        sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
        onChange={(e) => {
          e.target.name = SELFSERVICE_FIELD_NAMES.Question;
          dependencyChangeHandler(e);
        }}
        value={localState.question}
        disabled={questionsToChooseFrom.length === 0}
      >
        <Option value={null}>Select</Option>
        {questionsToChooseFrom
          .sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0))
          .map((que) => {
            return (
              <Option key={que.code} value={que.code} disabled={isCountryDisabled(que)}>
                {que.text}
              </Option>
            );
          })}
      </SelectField>
      <SelectField
        label={SELFSERVICE_FIELD_NAMES.Selections}
        sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}
        multiple
        onChange={(e) => {
          e.target.name = SELFSERVICE_FIELD_NAMES.Selections;
          dependencyChangeHandler(e);
        }}
        value={localState.selections}
        renderValue={(selected) =>
          selected && selected.length > 0
            ? selectionsToChooseFrom.filter((sel) => selected.includes(String(sel.value))).map((sel) => sel.text).join(", ")
            : "Select"
        }
        disabled={selectionsToChooseFrom.length === 0}
      >
        {selectionsToChooseFrom.sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0)).map((sel) => (
          <Option key={sel.code} value={String(sel.value)}>
            <Checkbox checked={localState.selections.includes(String(sel.value))} />
            {sel.text}
          </Option>
        ))}
      </SelectField>
    </Grid>
  );
};

export default DependencyTab;
